import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Store} from '@ngrx/store';
import {RouterSelectors} from '@aviary/store/router/router.selectors';
import {QueryTypeUpdate, SearchSubheaderQueryGroups, SearchSubheaderQueryTypes} from '@aviary/components/search/search.types';
import {isArray, isPopulatedString} from '@aviary/store/common/typing.helpers';
import {MatOptgroup, MatOption, MatSelect} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {NgClass} from '@angular/common';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

interface QueryTypeOption {
  queryType: string;
  displayableName: string;
}

interface QueryTypeOptionGroup {
  name: string;
  options: QueryTypeOption[];
}

type QueryTypeSetBy = 'PREFERENCE' | 'FIRST_AVAILABLE' | 'QUERY_PARAM' | 'USER_INPUT';

@Component({
  selector: 'mx-search-controls',
  host: {
    class: 'flex flex-row w-full'
  },
  templateUrl: './search-controls.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSelect, FormsModule, MatOptgroup, MatOption, MatInput, NgClass, FilledButtonComponent, IconButtonComponent]
})
export class SearchControlsComponent implements OnInit {

  @ViewChild('input') set input(element: ElementRef<HTMLInputElement>) {
    this.searchInputElement = element?.nativeElement;
    this.searchInputElement?.focus();
  }

  @Input() public showSearchType = true;
  @Input() public queryTypes: SearchSubheaderQueryTypes | SearchSubheaderQueryGroups;
  @Input() public placeholderText: string = 'Search or Scan';
  @Input() public showDropShadows: boolean = true;
  @Input() public showBorder: boolean = false;
  @Input() public typeParamNameOverride?: string;
  @Input() public hasMinWidth: boolean = true;
  @Input() public updateFromRouter: boolean = false;

  private _queryTypeDefault?: string;
  @Input()
  public get queryTypeDefault(): string | undefined {
    return this._queryTypeDefault;
  }

  public set queryTypeDefault(queryTypeDefault: string | undefined) {
    if (isPopulatedString(queryTypeDefault)) {
      this._queryTypeDefault = queryTypeDefault;

      // If the query type hasn't already been definitively set (URL param, user input, or preference), and it's a valid option, then reinitialize to use the
      // supplied default.
      if (this.lastSetQueryTypeVia === 'FIRST_AVAILABLE' && this.isOptionValid(queryTypeDefault)) {
        this.initializeQueryType();
        this.setQueryType();
        this.queryTypeChange.emit({queryType: queryTypeDefault, wasUserSelection: false});
      }
    }
  }

  @Output() public readonly queryTypeChange: EventEmitter<QueryTypeUpdate> = new EventEmitter<QueryTypeUpdate>();
  @Output() public readonly searchSubmit: EventEmitter<string> = new EventEmitter<string>();

  public query: string;
  public queryType: string;
  public selectValue: string;
  public options: QueryTypeOption[];
  public groupedOptions: QueryTypeOptionGroup[];
  public showClearButton = false;

  private searchInputElement: HTMLInputElement;
  private readonly store: Store = inject(Store);
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private lastSetQueryTypeVia: QueryTypeSetBy = 'FIRST_AVAILABLE';
  private paramsInitialized: boolean = false;

  public ngOnInit(): void {
    if (this.isGroupedOptions(this.queryTypes)) {
      this.groupedOptions = this.createQueryGroupOptions(this.queryTypes);
    } else {
      this.options = this.createQueryTypeOptions(this.queryTypes);
    }

    const queryTypeParamName: string = this.typeParamNameOverride ?? 'qType';
    this.store.select(RouterSelectors.queryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const query = params['q'] ?? ''
        const queryType = params[queryTypeParamName];

        if (!this.paramsInitialized) {
          // Initialize the query and queryParam values once from the params.
          this.paramsInitialized = true;
          this.query = query;
          this.initializeQueryType(queryType);
          this.setQueryType();
        } else if (this.updateFromRouter) {
          // If updating from queryParams is enabled, update the queryType and query values from the params any time they change.
          this.initializeQueryType(queryType);
          this.setQueryType();
          this.queryTypeChange.emit({queryType: this.selectValue, wasUserSelection: false, skipSearch: true});
          this.changeDetector.markForCheck();

          this.query = query;
          this.showClearButton = isPopulatedString(query);
          this.changeDetector.markForCheck();
        }
      });

    // If we initialize with a queryType or query already in the URL, inform our parent component.
    if (this.queryType) this.queryTypeChange.emit({queryType: this.queryType, wasUserSelection: false});

    if (this.query) {
      this.searchSubmit.emit(this.query);
      this.showClearButton = true;
    }
  }

  public onKeydown(event: KeyboardEvent): void {
    /*
      We don't want to do anything but clear the input when the clear button is focused (at which point, Enter also triggers the button's (click) handler).
      If we wait for keyup, it's too late, and the event gets to the input element, too -- causing the immediate search we're trying to avoid.
    */
    if (event.key === 'Enter' && event.target === this.searchInputElement) {
      this.onClickSearchButton();
    }
  }

  public onKeyup(): void {
    // We can't do this on keydown, because it's too early -- this.query won't have been updated until after keyup.
    this.showClearButton = !!this.query;
  }

  public onClickClearButton(): void {
    this.query = '';
    this.showClearButton = false;
    this.searchInputElement?.focus();
  }

  public onSelectionChangeForSearchType(): void {
    this.lastSetQueryTypeVia = 'USER_INPUT';
    this.setQueryType();
    this.queryTypeChange.emit({queryType: this.selectValue, wasUserSelection: true});
  }

  public onClickSearchButton(): void {
    this.searchSubmit.emit(this.query);
  }

  private createQueryTypeOptions(queryTypes: SearchSubheaderQueryTypes): QueryTypeOption[] {
    return Object.entries(queryTypes).reduce<QueryTypeOption[]>(
      (accumulator, [value, displayableName]) => [...accumulator, {displayableName, queryType: value}],
      []
    );
  }

  private createQueryGroupOptions(queryGroups: SearchSubheaderQueryGroups): QueryTypeOptionGroup[] {
    const optionGroups: QueryTypeOptionGroup[] = [];
    for (const group of queryGroups) {
      optionGroups.push({name: group.name, options: this.createQueryTypeOptions(group.options)});
    }
    return optionGroups;
  }

  private initializeQueryType(queryParam?: string): void {
    if (queryParam) {
      this.selectValue = queryParam;
      this.lastSetQueryTypeVia = 'QUERY_PARAM';
    } else {
      this.selectValue = this.getDefaultOrFirstQueryType();
    }
  }

  private getDefaultOrFirstQueryType(): string {
    // If we were provided a default query type (for instance, from a user preference), use that.
    if (this.queryTypeDefault) {
      this.lastSetQueryTypeVia = 'PREFERENCE';
      return this.queryTypeDefault;
    }

    // Otherwise, return the first query type.
    this.lastSetQueryTypeVia = 'FIRST_AVAILABLE';
    if (this.hasGroups) {
      return `${this.groupedOptions[0]?.name}:${this.groupedOptions[0]?.options[0]?.queryType}`;
    }
    return this.options[0]?.queryType;
  }

  // Type guard to let typescript know which queryType format was passed in, a regular list or a grouped list.
  private isGroupedOptions(queryTypes: SearchSubheaderQueryTypes | SearchSubheaderQueryGroups): queryTypes is SearchSubheaderQueryGroups {
    return isArray(queryTypes);
  }

  private setQueryType() {
    this.queryType = this.selectValue.includes(':') ? this.selectValue.split(':')[1] : this.selectValue;
  }

  public get hasGroups() {
    return this.isGroupedOptions(this.queryTypes);
  }

  private isOptionValid(optionValue: string): boolean {
    if (this.hasGroups) {
      return this.groupedOptions?.some(group => group.options?.some(option => option.queryType === optionValue));
    }
    return this.options?.some(option => option.queryType === optionValue);
  }
}
